// Calculator state -> Salesforce save request. Pure: no store, no storage, no fetch, so the seam
// "what would autosave send for this sizing" is testable without rendering anything.
//
//   snapshot (Story_JSON__c)  the v3 value story, `v: 2` (a rollback that only knows v1 refuses it)
//   Total_Annual_Value__c     PROGRAM value (after realization, before attribution)
//   Clay-attributed value     lives only in the story JSON until the Salesforce fields exist
//   observations              one Value_Metric__c per enabled leading metric (`uc-*` keys), holding
//                             the today value; the server skips a key with no active definition
//
// Also owns the autosave dirty-check fingerprint. It is built from the whole sizing including each
// use case's realization and attribution, which v2's fingerprint missed (a factor-only edit never
// saved).

import type { SaveSnapshotBody } from '../api-client';
import { bmEff } from '../engine/benchmarks-v3';
import type { BmRow } from '../engine/migrate-v3';
import {
  enabledLevels,
  enabledMethods,
  orderSelection,
  type CalculatorInput,
} from '../engine/use-case-engine';
import { extractUseCaseObservations } from '../engine/use-case-observations';
import { buildValueStory, type StoryProof, type ValueStory } from '../engine/value-story';
import { getSizedUseCase } from '../data/use-case-catalog';
import type { AccountSelection } from '../flow/step-model';

export type LinkedAccountSelection = Extract<AccountSelection, { kind: 'account' }>;

/** Proof points kept per use case in the story (the deck never reads the benchmark table). */
const PROOFS_PER_USE_CASE = 3;

/**
 * The top benchmark rows per use case, as the story carries them. Rows with a result to quote
 * (an improvement or a "post" value) come first, then the rest, in table order.
 */
export function proofsByUseCase(
  rows: readonly BmRow[],
  useCaseIds: readonly string[],
): Record<string, StoryProof[]> {
  const out: Record<string, StoryProof[]> = {};
  for (const id of useCaseIds) {
    const mine = rows.filter((r) => r.metricId === id && !r.hidden);
    const quotable = (r: BmRow) => (r.imp || r.post ? 1 : 0);
    const sorted = [...mine].sort((a, b) => quotable(b) - quotable(a));
    out[id] = sorted.slice(0, PROOFS_PER_USE_CASE).map((r) => ({
      customer: r.customer,
      pre: r.pre,
      post: r.post,
      imp: bmEff(r),
      notes: r.notes,
    }));
  }
  return out;
}

export interface StoryBuildOptions {
  /** Epoch ms stamped on the story. Injected so the builder stays pure. */
  created: number;
  customerName: string;
  benchmarkRows?: readonly BmRow[];
}

/** The v3 value story for a sizing. */
export function storyForSizing(sizing: CalculatorInput, opts: StoryBuildOptions): ValueStory {
  const ids = sizing.selected.map((s) => s.id);
  return buildValueStory({
    deal: sizing.deal,
    selected: sizing.selected,
    tools: sizing.tools,
    customTools: sizing.customTools,
    created: opts.created,
    customerName: opts.customerName,
    stories: proofsByUseCase(opts.benchmarkRows ?? [], ids),
  });
}

/** The comma-joined first three use case names, as v2 wrote `Headline_Summary__c`. */
export function headlineSummary(story: ValueStory): string {
  return story.useCases
    .slice(0, 3)
    .map((u) => u.name)
    .join(', ');
}

/** The exact body `saveSnapshot()` sends for a linked account. */
export function buildSaveRequest(
  account: LinkedAccountSelection,
  sizing: CalculatorInput,
  opts: Omit<StoryBuildOptions, 'customerName'>,
): SaveSnapshotBody {
  const story = storyForSizing(sizing, { ...opts, customerName: account.accountName });
  return {
    accountId: account.accountId,
    ...(account.opportunityId ? { opportunityId: account.opportunityId } : {}),
    customerName: account.accountName,
    storySnapshot: story,
    totalAnnualValue: story.rollup.program,
    headlineSummary: headlineSummary(story),
    observations: extractUseCaseObservations(story),
  };
}

/* ------------------------------------------------------------------------ fingerprint */

const blankToNull = <T>(v: T | '' | null | undefined): T | null => (v === '' || v === undefined ? null : v);

function sortedEntries(rec: Record<string, number | ''> | null | undefined): [string, number | ''][] | null {
  return rec == null ? null : Object.entries(rec).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

/**
 * A content fingerprint of everything that changes what a save would write. Canonical on purpose:
 * selection order, key order and "blank means default" spellings do not change it, while any
 * answer, record level, tool, deal number, realization or attribution does.
 */
export function sizingFingerprint(sizing: CalculatorInput): string {
  const { deal } = sizing;
  return JSON.stringify({
    deal: [deal.acv, deal.winRate, blankToNull(deal.contractEstimate), blankToNull(deal.currentAnnualPipeline)],
    selected: orderSelection([...sizing.selected]).map((s) => {
      const uc = getSizedUseCase(s.id);
      return [
        s.id,
        uc ? enabledLevels(uc, s.recordLevels) : (s.recordLevels ?? null),
        uc ? enabledMethods(uc, s.methods) : (s.methods ?? null),
        sortedEntries(s.answers),
        blankToNull(s.realization),
        blankToNull(s.attribution),
      ];
    }),
    tools: sortedEntries(sizing.tools),
    customTools: (sizing.customTools ?? []).map((t) => [t.name, t.spend]),
  });
}
